import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { GetTripQry, GetTripTicketQry, TripClusterModel, TripFilterOptions, TripStatus } from '../models/Trip'
import { clientTimeZoneOffset, TicketStatus } from 'app/utils/common/constants'

// The manual-routing cooldown end time (provided by the backend as `until`)
// must survive a tab close/reload, so it is mirrored into localStorage
// (redux state alone is in-memory). Stored as epoch ms.
export const MANUAL_ROUTE_COOLDOWN_UNTIL_KEY = 'trip:manualRouteCooldownUntil'

const readPersistedCooldownUntil = (): number | null => {
  try {
    const raw = localStorage.getItem(MANUAL_ROUTE_COOLDOWN_UNTIL_KEY)
    const until = raw ? Number(raw) : NaN
    // Ignore an already-expired cooldown.
    return Number.isFinite(until) && until > Date.now() ? until : null
  } catch {
    return null
  }
}

const persistCooldownUntil = (until: number | null) => {
  try {
    if (until && until > Date.now()) {
      localStorage.setItem(MANUAL_ROUTE_COOLDOWN_UNTIL_KEY, String(until))
    } else {
      localStorage.removeItem(MANUAL_ROUTE_COOLDOWN_UNTIL_KEY)
    }
  } catch {
    // ignore storage errors (e.g. private mode)
  }
}

// Recompute a trip's aggregate status + hard flag from the state of its tickets.
// Shared by updateTicketStatus and syncTicketStatusById so both entry points keep
// the trip row (status badge + hard flag) consistent with its tickets.
const recomputeTripAggregates = (trip: GetTripQry, tickets: GetTripTicketQry[]) => {
  const currentTime = new Date()

  // Check if all tickets are completed
  const isAllCompleted = tickets.every(t => t.status.startsWith(TicketStatus.Completed))

  // Check if all tickets are cancelled
  const isAllCancelled = tickets.every(
    t => t.status.startsWith(TicketStatus.Canceled) ||
      t.status === TicketStatus.CanceledByB8akAgent ||
      t.status === TicketStatus.CanceledByCustomer
  )

  // Check if all tickets have not started delivery time yet
  const allNotStarted = tickets.every(
    t => t.deliveryStart && new Date(t.deliveryStart) > currentTime
  )

  // Check if any ticket is in progress: not completed/cancelled AND within delivery time window
  const hasInProgress = tickets.some(t => {
    const isNotCompletedOrCancelled = !t.status.startsWith(TicketStatus.Completed) &&
      !t.status.startsWith(TicketStatus.Canceled) &&
      t.status !== TicketStatus.CanceledByB8akAgent &&
      t.status !== TicketStatus.CanceledByCustomer

    if (!isNotCompletedOrCancelled) return false

    // Check if ticket is within delivery time window
    if (t.deliveryStart && t.deliveryEnd) {
      const deliveryStartTime = new Date(t.deliveryStart)
      const deliveryEndTime = new Date(t.deliveryEnd)
      return currentTime >= deliveryStartTime && currentTime <= deliveryEndTime
    }

    return false
  })

  // Update trip status
  if (isAllCompleted) {
    // trip.status = TripStatus.COMPLETED
  } else if (isAllCancelled) {
    // trip.status = TripStatus.CANCELLED
  } else if (hasInProgress) {
    trip.status = TripStatus.IN_PROGRESS
  } else if (allNotStarted) {
    trip.status = TripStatus.PLANNED
  }

  // Check and update hard flag
  // Hard flag if any ticket is not Delivered before delivery window ends
  const hasUndeliveredAfterWindow = tickets.some(t => {
    if (!t.deliveryEnd) return false
    const deliveryEndTime = new Date(t.deliveryEnd)
    const isAfterWindow = currentTime > deliveryEndTime
    const isNotDelivered = t.status !== TicketStatus.Delivered &&
      !t.status.startsWith(TicketStatus.Completed)
    return isAfterWindow && isNotDelivered
  })

  trip.hardFlag = hasUndeliveredAfterWindow
}

export interface TripState {
  trips: GetTripQry[]
  totalTrips: number
  tripTickets: Record<string, GetTripTicketQry[]> // tripId -> tickets
  ticketsLoading: Record<string, boolean> // tripId -> loading state for its tickets
  loading: boolean
  error: string | null
  filters: TripFilterOptions
  selectedTripId: string | null,
  delayReRoute: boolean,
  clusters: TripClusterModel[],
  exporting: boolean,
  unassignedTickets: GetTripTicketQry[],
  manualRoutingEnabled: boolean, // whether the backend allows manual routing (polled)
  unassignedTicketCount: number, // unassigned-ticket count from the poll (button badge)
  manualRouting: boolean, // a manual-route request is in flight
  manualRouteCooldownUntil: number | null, // epoch ms when the cooldown ends (from backend `until`)
  manualRouteSuccessCount: number, // tickets accepted by the engine on the last request
}

const initialState: TripState = {
  trips: [],
  totalTrips: 0,
  tripTickets: {},
  ticketsLoading: {},
  loading: false,
  error: null,
  filters: {
    serviceDate: new Date(),
    status: '',
    clusterId: undefined,
    search: '',
    clientTimeZone: clientTimeZoneOffset,
    language: "EN",
    housingId: 0,
    cityId: 0,
  },
  selectedTripId: null,
  delayReRoute: false,
  clusters: [],
  exporting: false,
  unassignedTickets: [],
  manualRoutingEnabled: false,
  unassignedTicketCount: 0,
  manualRouting: false,
  manualRouteCooldownUntil: readPersistedCooldownUntil(),
  manualRouteSuccessCount: 0,
}

export const tripSlice = createSlice({
  name: 'trip',
  initialState,
  reducers: {
    // Loading states
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
    },

    // Trips data
    setTrips: (state, action: PayloadAction<{ trips: GetTripQry[]; totalTrips: number }>) => {
      state.trips = action.payload.trips
      state.totalTrips = action.payload.totalTrips
    },
    clearTrips: (state) => {
      state.trips = []
      state.totalTrips = 0
    },

    // Trip tickets data
    setTripTickets: (state, action: PayloadAction<{ tripId: string; tickets: GetTripTicketQry[] }>) => {
      state.tripTickets[action.payload.tripId] = action.payload.tickets
    },
    setTripTicketsLoading: (state, action: PayloadAction<{ tripId: string; loading: boolean }>) => {
      state.ticketsLoading[action.payload.tripId] = action.payload.loading
    },
    clearTripTickets: (state, action: PayloadAction<string>) => {
      delete state.tripTickets[action.payload]
    },
    clearAllTripTickets: (state) => {
      state.tripTickets = {}
    },
    updateTripStatus: (
      state,
      action: PayloadAction<{
        tripId: string
        status: string,
        hardFlag: boolean
      }>
    ) => {
      const { tripId, status } = action.payload

      const trip = state.trips.find(t => t.id === tripId)
      if (trip) {
        trip.status = status
        if (action.payload.hardFlag !== undefined) {
          trip.hardFlag = action.payload.hardFlag
        }
      }
    },

    // Filters
    setFilters: (state, action: PayloadAction<Partial<TripFilterOptions>>) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    resetFilters: (state) => {
      state.filters = initialState.filters
    },

    // Selected trip
    setSelectedTripId: (state, action: PayloadAction<string | null>) => {
      state.selectedTripId = action.payload
    },

    // reroute
    setDelayReRoute: (state, action) => {
      state.delayReRoute = action.payload
    },

    // trip filter cluster  

    // Trips data
    setClusters: (state, action: PayloadAction<TripClusterModel[]>) => {
      state.clusters = action.payload
    },
    clearClusters: (state) => {
      state.clusters = []
    },

    toggleTicketLog: (
      state,
      action: PayloadAction<{
        tripId: string
        ticketId: string
      }>
    ) => {
      const { tripId, ticketId } = action.payload
      console.log("ticketId", ticketId);

      const tickets = state.tripTickets[tripId]
      if (!tickets) return

      const ticket = tickets.find(t => t.id === ticketId)
      if (ticket) {
        ticket.isShowLog = !ticket.isShowLog
      }
    },
    updateTicketStatus: (
      state,
      action: PayloadAction<{
        tripId: string
        ticketId: string
        status: string
      }>
    ) => {
      const { tripId, ticketId, status } = action.payload

      const tickets = state.tripTickets[tripId]
      if (!tickets) return

      const ticket = tickets.find(t => t.id === ticketId)
      if (ticket) {
        ticket.status = status
      }

      const trip = state.trips.find(t => t.id === tripId)
      if (!trip) return

      // Recompute the trip's aggregate status + hard flag from all its tickets.
      recomputeTripAggregates(trip, tickets)
    },

    updateTripTicket: (
      state,
      action: PayloadAction<{
        tripId: string
        ticketId: string
        data: Partial<GetTripTicketQry>
      }>
    ) => {
      const { tripId, ticketId, data } = action.payload

      const tickets = state.tripTickets[tripId]
      if (!tickets) return

      const ticket = tickets.find(t => t.id === ticketId)
      if (!ticket) return

      // Update các field được truyền vào
      Object.assign(ticket, data)

      const trip = state.trips.find(t => t.id === tripId)
      if (!trip) return

      // Recompute lại thông tin của trip
      recomputeTripAggregates(trip, tickets)
    },

    // Sync a single ticket's status by ticket id, regardless of which trip it
    // belongs to (or whether it's still unassigned). Dispatched from the ticket
    // detail modals (ViewPackageModal / ViewTicketModal) after an attendance or
    // delivery status change, so the Trip list "Ticket Status" column and the
    // Manual Routing modal "Status" column reflect the change immediately.
    syncTicketStatusById: (
      state,
      action: PayloadAction<{
        ticketId: string
        status: string
      }>
    ) => {
      const { ticketId, status } = action.payload

      // The real ticket GUID (what the modal passes as `ticketId`) is stored in
      // different fields depending on the list: trip tickets keep it in
      // `ticketId` (their `id` is the trip-ticket row id), while unassigned
      // tickets keep it in `id`. Match on either so both stay in sync.
      const matchesTicket = (t: GetTripTicketQry) =>
        t.ticketId === ticketId || t.id === ticketId

      // Update the ticket inside whichever trip contains it, then recompute
      // that trip's aggregate status + hard flag.
      Object.keys(state.tripTickets).forEach((tripId) => {
        const tickets = state.tripTickets[tripId]
        const ticket = tickets.find(matchesTicket)
        if (!ticket) return

        ticket.status = status

        const trip = state.trips.find(t => t.id === tripId)
        if (trip) recomputeTripAggregates(trip, tickets)
      })

      // Update the unassigned-tickets list backing the Manual Routing modal.
      const unassignedTicket = state.unassignedTickets.find(matchesTicket)
      if (unassignedTicket) {
        unassignedTicket.status = status
      }
    },
    // Reset all
    resetTripState: () => initialState,

    //export csv

    requestExportTripCSVSuccess(state) {
      state.exporting = false
    },

    requestExportTripCSVFail(state) {
      state.exporting = false
    },

    // unassigned tickets
    setUnassignedTickets: (state, action: PayloadAction<GetTripTicketQry[]>) => {
      state.unassignedTickets = action.payload
    },
    clearUnassignedTickets: (state) => {
      state.unassignedTickets = []
    },
    // Result of the check-enabled-manual-routing poll.
    setManualRoutingStatus: (
      state,
      action: PayloadAction<{ isEnabled: boolean; unassignedTicket: number }>
    ) => {
      state.manualRoutingEnabled = action.payload.isEnabled
      state.unassignedTicketCount = action.payload.unassignedTicket
    },

    // manual routing
    setManualRouting: (state, action: PayloadAction<boolean>) => {
      state.manualRouting = action.payload
    },
    setManualRouteSuccessCount: (state, action: PayloadAction<number>) => {
      state.manualRouteSuccessCount = action.payload
    },
    // Cooldown end time pushed by the backend (`until`), in epoch ms.
    setManualRouteCooldownUntil: (state, action: PayloadAction<number | null>) => {
      state.manualRouteCooldownUntil = action.payload
      persistCooldownUntil(action.payload)
    },
  },
})

// Actions
export const {
  setLoading,
  setError,
  setTrips,
  clearTrips,
  setTripTickets,
  setTripTicketsLoading,
  clearTripTickets,
  clearAllTripTickets,
  setFilters,
  resetFilters,
  setSelectedTripId,
  resetTripState,
  setDelayReRoute,
  setClusters,
  clearClusters,
  toggleTicketLog,
  updateTicketStatus,
  syncTicketStatusById,
  requestExportTripCSVSuccess,
  requestExportTripCSVFail,
  setUnassignedTickets,
  clearUnassignedTickets,
  setManualRoutingStatus,
  updateTripStatus,
  updateTripTicket,
  setManualRouting,
  setManualRouteSuccessCount,
  setManualRouteCooldownUntil,
} = tripSlice.actions

// Selectors - Note: RootState will be augmented by RootReducer
export const selectTrips = (state: any) => state.trip.trips
export const selectTotalTrips = (state: any) => state.trip.totalTrips
export const selectTripTickets = (state: any, tripId: string) => state.trip.tripTickets[tripId] || []
export const selectTripTicketsLoading = (state: any, tripId: string): boolean | undefined =>
  state.trip.ticketsLoading[tripId]
export const selectTripLoading = (state: any) => state.trip.loading
export const selectTripError = (state: any) => state.trip.error
export const selectTripFilters = (state: any) => state.trip.filters
export const selectSelectedTripId = (state: any) => state.trip.selectedTripId
export const selectDelayReRoute = (state: any) => state.trip.delayReRoute
export const selectClusters = (state: any) => state.trip.clusters
export const selectUnassignedTickets = (state: any) => state.trip.unassignedTickets
export const selectManualRoutingEnabled = (state: any) => state.trip.manualRoutingEnabled
export const selectUnassignedTicketCount = (state: any) => state.trip.unassignedTicketCount
export const selectManualRouting = (state: any) => state.trip.manualRouting
export const selectManualRouteCooldownUntil = (state: any) => state.trip.manualRouteCooldownUntil
export const selectManualRouteSuccessCount = (state: any) => state.trip.manualRouteSuccessCount

// Export reducer with named export (matching other modules)
export const tripReducer = tripSlice.reducer
export default tripSlice.reducer

